import { isProductionEnvironment } from "mt-shared/environment";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import Head from "next/head";
import Router, { useRouter } from "next/router";
import Script from "next/script";
import NProgress from "nprogress";
import { ReactElement, ReactNode, useEffect, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { SWRConfig } from "swr";
import { AnalyticsProvider } from "../components/Atoms/Analytics";
import { CloudinaryProvider } from "../components/Atoms/Cloudinary";
import { ToastProvider } from "../components/Atoms/Toast";
import { SearchProvider } from "../components/Cards/SearchBar/SearchProvider";
import Layout from "../components/Layout";
import LayoutProvider from "../components/Layout/provider";
import { useAfterInteractionOrDelay } from "../hooks/useAfterInteractionOrDelay";
import { useGbuuidUserProperty } from "../hooks/useGbuuidUserProperty";
import { useInteract } from "../hooks/useInteract";
import "../styles/globals.scss";
import "../styles/nprogress.css";
import * as analytics2 from "../utils/analytics2";
import { CACHED_GQL_FALLBACK_PROP_NAME } from "../utils/cachedGqlKey";

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const isDev = () => !isProductionEnvironment;

Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

const analyticsTracker = isDev()
  ? analytics2.defaultTracker
  : analytics2.tracker;

const HeaderMeta = () => {
  return (
    <Head>
      <meta name="application-name" content="MagicalTrip" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="mobile-web-app-capable" content="yes" />
    </Head>
  );
};

// Analytics and marketing tags load after the page, not during the initial load:
// before LCP they competed with the page for the network and the main thread.
// - GA4 (gtag.js) loads once the page has loaded and the browser is idle
//   (lazyOnload), so even short visits are tracked. One gtag.js serves both GA4
//   properties: pages/_document.js configures both IDs in its inline stub.
// - GTM and every tag it loads (ads, pixels, session recording) load on the
//   visitor's first interaction, or INTERACTION_FALLBACK_DELAY_MS after the load
//   event if there is none. A fixed delay alone wasn't enough: pages that keep loading longer (City,
//   Product) still had GTM inside Lighthouse's measurement at 3 s, adding 400+ ms of
//   Total Blocking Time. Visits that leave without interacting within that time
//   aren't seen by GTM's tags.
// Events sent earlier are queued by the inline `gtag` / `dataLayer` stub in
// pages/_document.js and processed when the scripts load.
function AnalyticsTags() {
  const canLoadGtm = useAfterInteractionOrDelay();

  return (
    <>
      <Script
        strategy="lazyOnload"
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.MT_GA4_ID}`}
      />
      {canLoadGtm && (
        <Script id="gtm" strategy="afterInteractive">
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-${process.env.GTM_ID}');`}
        </Script>
      )}
    </>
  );
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();
  const swrConfig = useMemo(
    () => ({ fallback: pageProps[CACHED_GQL_FALLBACK_PROP_NAME] ?? {} }),
    [pageProps],
  );

  const getLayout = Component.getLayout ?? ((page) => <Layout>{page}</Layout>);

  const cloudinaryAutoUploadMappings = useMemo(
    () => [
      {
        folder: process.env.CLOUDINARY_AUTO_UPLOAD_MAPPING_FOLDER as string,
        urlPrefix: process.env.CLOUDINARY_S3_MAPPING_URL_PREFIX as string,
      },
    ],
    [],
  );

  const interacted = useInteract();

  const tabQueryValue = Array.isArray(router.query.tab)
    ? router.query.tab[0]
    : router.query.tab;
  const isPortalChatTab =
    router.pathname === "/portal/[bookingId]" && tabQueryValue === "chat";
  const pagePropsRequestHidingZendesk = pageProps.shouldHideZendesk === true;
  const shouldDisableZendesk = useMemo(
    () =>
      pagePropsRequestHidingZendesk ||
      isPortalChatTab ||
      router.pathname === "/guide/[guideId]/sign-contracts" ||
      router.pathname === "/guide/sign-contracts-success" ||
      router.pathname === "/bookings/[id]/chat" ||
      router.pathname === "/recruit" ||
      router.pathname === "/recruit/form" ||
      router.pathname === "/performance-test" ||
      router.pathname.startsWith("/performance-test/"),
    [pagePropsRequestHidingZendesk, isPortalChatTab, router.pathname],
  );

  const isPerformanceTest = useMemo(
    () =>
      router.pathname === "/performance-test" ||
      router.pathname.startsWith("/performance-test/"),
    [router.pathname],
  );

  useGbuuidUserProperty();

  useEffect(() => {
    const zendesk = (window as any).zE;
    if (!zendesk) return;

    zendesk("messenger", shouldDisableZendesk ? "hide" : "show");
  }, [shouldDisableZendesk]);

  return (
    <SWRConfig value={swrConfig}>
      <CloudinaryProvider
        cloudName={process.env.CLOUDINARY_CLOUD_NAME}
        autoUploadMappingFolder={cloudinaryAutoUploadMappings as any}
      >
        {isProductionEnvironment && !isPerformanceTest && <AnalyticsTags />}
        <AnalyticsProvider tracker={analyticsTracker}>
          <ToastProvider>
            <SearchProvider
              disabled={
                isPerformanceTest &&
                router.pathname !== "/performance-test/header" &&
                router.pathname !== "/performance-test/footer"
              }
            >
              <LayoutProvider>
                <HeaderMeta />
                {getLayout(<Component {...pageProps} />)}
              </LayoutProvider>
            </SearchProvider>
          </ToastProvider>
        </AnalyticsProvider>
      </CloudinaryProvider>
      {!shouldDisableZendesk && interacted && (
        <Script
          id="ze-snippet"
          src="https://static.zdassets.com/ekr/snippet.js?key=719d4c42-0ef0-47ca-8d39-0374fc99264c"
          strategy="afterInteractive"
          defer
        />
      )}
    </SWRConfig>
  );
}

export default MyApp;
